import { InputRichText, Tooltip } from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { Channel } from 'lib-shared-types-channels';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useUnmount } from 'react-use';

import { aiApi } from 'apis/ai';
import { ConversationContext } from 'components/conversation';
import { copilotUtils } from 'utils/copilot';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useEditorContext } from 'utils/useEditorContext';

import { PinnedActionProps } from '../copilotPinnedActions.types';

import { UIBlockerError } from './uiBlockerError';
import { UIBlockerLoading } from './uiBlockerLoading';

export const ExpandText = ({
  channel,
  text,
  label,
}: PinnedActionProps): JSX.Element | null => {
  const { content } = useContext(InputRichText.Context);
  const { replace } = useEditorContext();
  const { hasCopilot } = useCopilotTrial();
  const triggerRef = useRef<ReturnType<typeof improveReply> | null>(null);

  const [improveReply, { status }] =
    aiApi.useCreateAiWritingImprovementMutation();

  const { isDisabled, tooltipContent } = copilotUtils.expandText.getStatus({
    text,
    status,
  });

  const { conversation } = useContext(ConversationContext);

  const lastMessage = useMemo(() => {
    const events = conversation?.events || [];
    const messages = events.filter(
      (event) =>
        event.type === 'conversation:message_added' &&
        event.direction === 'inbound',
    );

    if (messages.length === 0) return '';
    if (!messages[messages.length - 1].text) return '';
    return messages[messages.length - 1].text as string;
  }, [conversation]);

  useUnmount(() => {
    if (triggerRef.current) {
      triggerRef.current.abort();
    }
  });

  const onClickHandler = useCallback(() => {
    content.setUiBlocker(<UIBlockerLoading loadingText={'Expanding text'} />);

    const trigger = improveReply({
      lastMessage,
      text,
      promptName: 'expand_reply',
      contexts: {
        features: 'impr_reply',
        ...(channel && {
          channels: convertChannelCasing(channel, 'snake') as Channel,
        }),
      },
      csid: conversation?.id?.toString(),
    });

    triggerRef.current = trigger;

    trigger
      .unwrap()
      .then((data) => {
        triggerRef.current = null;
        if (data.text) {
          replace(data?.text ?? '');
        }
        content.setUiBlocker(null);
      })
      .catch(() => {
        triggerRef.current = null;
        content.setUiBlocker(
          <UIBlockerError
            onClick={() => {
              content.setUiBlocker(null);
            }}
            errorText={
              label ? `Failed to ${label.toLowerCase()}` : 'Failed to expand'
            }
          />,
        );
      });
  }, [
    content,
    improveReply,
    lastMessage,
    text,
    channel,
    conversation?.id,
    replace,
    label,
  ]);

  if (!hasCopilot) {
    return null;
  }

  return (
    <Tooltip content={tooltipContent}>
      <InputRichText.ToolbarButton
        icon="chat-plus"
        onClick={onClickHandler}
        disabled={isDisabled}
      />
    </Tooltip>
  );
};
